// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2, Star, Phone, Mail, MapPin, Briefcase } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import { useVendorDetail, useDeleteVendor } from '@/hooks/useCmms'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { WorkOrder } from '@/types'

export function VendorDetailPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const vendorId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()

  const { data: vendor, isLoading, isFetching } = useVendorDetail(vendorId)
  const deleteVendor = useDeleteVendor()

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const handleDelete = async () => {
    await deleteVendor.mutateAsync(vendorId)
    navigate('/vendors')
  }

  const getWorkOrderStatusBadge = (wo: WorkOrder) => {
    const variants: Record<WorkOrder['status'], 'warning' | 'info' | 'success' | 'ghost'> = {
      open: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'ghost',
    }
    return <Badge variant={variants[wo.status]}>{t(`work_order_status.${wo.status}`)}</Badge>
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card><Skeleton className="h-48" /></Card>
      </div>
    )
  }

  if (!vendor) {
    return (
      <Card>
        <EmptyState
          title={t('empty.not_found')}
          action={<Button onClick={() => navigate('/vendors')}>{t('actions.back_to_list')}</Button>}
        />
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: t('vendors.title'), href: '/vendors' },
          { label: vendor.name },
        ]} />

        <PageHeader
          title={vendor.name}
          description={vendor.contact_name || ''}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'edit',
            label: tc('actions.edit'),
            icon: Edit,
            to: `/vendors/${id}/edit`,
          }}
          overflowActions={[
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              destructive: true,
              onClick: () => setShowDeleteModal(true),
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Info */}
          <Card className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('vendors.detail')}</h2>
              {vendor.active ? (
                <Badge variant="success">{tc('status.active')}</Badge>
              ) : (
                <Badge variant="ghost">{tc('status.inactive')}</Badge>
              )}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {vendor.contact_name && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60">{t('labels.contact_name')}</dt>
                  <dd className="font-medium break-words">{vendor.contact_name}</dd>
                </div>
              )}
              {vendor.phone && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <Phone className="w-4 h-4" /> {t('labels.phone')}
                  </dt>
                  <dd className="font-medium break-words">{vendor.phone}</dd>
                </div>
              )}
              {vendor.email && (
                <div className="min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <Mail className="w-4 h-4" /> {t('labels.email')}
                  </dt>
                  <dd className="font-medium break-words">{vendor.email}</dd>
                </div>
              )}
              {vendor.address && (
                <div className="col-span-full min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {t('labels.address')}
                  </dt>
                  <dd className="font-medium break-words">{vendor.address}</dd>
                </div>
              )}
              {vendor.specialties && vendor.specialties.length > 0 && (
                <div className="col-span-full min-w-0">
                  <dt className="text-sm text-base-content/60 flex items-center gap-1 mb-2">
                    <Briefcase className="w-4 h-4" /> {t('labels.specialties')}
                  </dt>
                  <dd className="flex flex-wrap gap-2">
                    {vendor.specialties.map((specialty, idx) => (
                      <Badge key={idx} variant="ghost">{specialty}</Badge>
                    ))}
                  </dd>
                </div>
              )}
            </dl>

            {vendor.notes && (
              <div className="border-t border-base-200 pt-4 mt-4">
                <h3 className="font-medium mb-2">{t('labels.notes')}</h3>
                <p className="text-base-content/70 whitespace-pre-wrap">{vendor.notes}</p>
              </div>
            )}
          </Card>

          {/* Score Card */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.avg_score')}</h2>
            {vendor.avg_score != null ? (
              <div>
                <div className="text-4xl font-bold text-primary mb-2">
                  {vendor.avg_score.toFixed(1)}
                </div>
                <div className="flex gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i <= Math.round(vendor.avg_score!) ? 'text-warning fill-warning' : 'text-base-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-base-content/60">{t('labels.quality_rating')}</p>
              </div>
            ) : (
              <p className="text-base-content/50 text-sm">-</p>
            )}
          </Card>
        </div>

        {/* Recent Work Orders */}
        {vendor.recent_work_orders && vendor.recent_work_orders.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('labels.recent_work_orders')}</h2>
            <div className="space-y-2">
              {vendor.recent_work_orders.map((wo) => (
                <button
                  key={wo.id}
                  onClick={() => navigate(`/work-orders/${wo.id}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-base-200 hover:border-primary hover:bg-base-200 transition-colors text-left"
                >
                  <div>
                    <div className="font-medium">{wo.title}</div>
                    <div className="text-sm text-base-content/60">
                      {formatDate(wo.created_at)}
                    </div>
                  </div>
                  {getWorkOrderStatusBadge(wo)}
                </button>
              ))}
            </div>
          </Card>
        )}
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('messages.delete_vendor_title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteVendor.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('messages.delete_vendor_confirm', { name: vendor.name })}</p>
      </Modal>
    </>
  )
}
